/** Translation text belongs to the package or plugin that renders it. */
export interface TranslationCatalog {
  readonly [key: string]: string | TranslationCatalog
}

export type TranslationCatalogSource = TranslationCatalog | (() => Promise<TranslationCatalog>)

export interface LocalizationResources<TNamespace extends string = string> {
  readonly namespace: TNamespace
  readonly resources: Readonly<Record<string, TranslationCatalogSource>>
}

/** Declares resources without registering anything on a global i18next instance. */
export function defineLocalizationResources<const TNamespace extends string>(
  options: LocalizationResources<TNamespace>
): LocalizationResources<TNamespace> {
  if (!options.namespace.trim() || options.namespace.trim() !== options.namespace || options.namespace.includes(":")) {
    throw new Error("A localization namespace must be nonempty, normalized, and contain no colon.")
  }
  const resources: Record<string, TranslationCatalogSource> = Object.create(null)
  for (const [language, source] of Object.entries(options.resources)) {
    const canonical = Intl.getCanonicalLocales(language)[0]
    if (!canonical) throw new Error("A translation language must not be empty.")
    if (Object.hasOwn(resources, canonical)) {
      throw new Error(`Duplicate translation language: ${canonical}.`)
    }
    resources[canonical] = source
  }
  if (!Object.keys(resources).length) {
    throw new Error(`Localization namespace ${options.namespace} has no resources.`)
  }
  return Object.freeze({ namespace: options.namespace, resources: Object.freeze(resources) })
}

/** Isolate resource stores: i18next must never mutate another application's catalog. */
export function copyTranslationCatalog(catalog: TranslationCatalog): TranslationCatalog {
  const result: Record<string, string | TranslationCatalog> = Object.create(null)
  for (const [key, value] of Object.entries(catalog)) {
    if (key === "__proto__" || key === "constructor") {
      throw new Error(`Invalid translation key: ${key}.`)
    }
    if (typeof value === "string") result[key] = value
    else if (value && typeof value === "object" && !Array.isArray(value)) {
      result[key] = copyTranslationCatalog(value)
    } else {
      throw new Error(`Translation ${key} must be text or a nested catalog.`)
    }
  }
  return result
}
